import { StripePaymentStatus } from './../billing/billing.models';
import { PagedRequestDto } from '../paged-request.model';
import { ScopeOfPracticeEnum } from '@umss/shared/utils';

export type PaymentMethodEnum =
  | 'ACH'
  | 'Wire'
  | 'Check'
  | 'CreditCard'
  | 'Cash'
  | 'Other';

export type DifferentialRuleType =
  | 'FacilityType'
  | 'ContractorScope'
  | 'Combined';

export type ContactMethod = 'Email' | 'PushNotification' | 'PhoneCall';

export interface InvoiceDto extends PagedRequestDto {
  facilityId: string;
  status: InvoiceStatus;
  invoiceDateFrom: string;
  invoiceDateTo: string;
  dueDateFrom: string;
  dueDateTo: string;
  amountFrom: number;
  amountTo: number;
  isOverdue: boolean;
  corporateId?: string;
  scopeOfPractice?: ScopeOfPracticeEnum;
}

export interface InvoiceSummaryDto {
  facilityId?: string;
  periodStartDate?: string;
  includeInvoices?: boolean;
  corporateId?: string;
}

export interface FacilityInvoice {
  createdAt: string;
  updatedAt: string;
  id: string;
  facilityId: string;
  facilityName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;
  status: InvoiceStatus;
  isOverdue: boolean;
  daysOverdue: number;
  timesheetsCount: number;
  periodStartDate: string;
  periodEndDate: string;
  paidDate: string | null;
  payPeriodFormatted: string;
  totalHoursScheduled: number;
  totalHoursWorked: number;
  stripePaymentStatus?: StripePaymentStatus;
  stripePaymentLinkUrl?: string;
}

export interface FacilityInvoiceDetail extends FacilityInvoice {
  description: string;
  lineItems: PaymentInvoiceLineItem[];
  payments: InvoicePayment[];
  timesheets: InvoiceTimesheet[];
}

export interface PaymentInvoiceLineItem {
  createdAt: string;
  updatedAt: string;
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  shiftDate: string;
  startTime: string | null;
  endTime: string | null;
}

export interface InvoicePayment {
  createdAt: string;
  updatedAt: string;
  id: string;
  invoiceId: string;
  amount: number;
  paymentMethod: PaymentMethodEnum;
  referenceNumber: string | null;
  paymentDate: string;
  notes: string | null;
  recordedByUserId: string;
  recordedByUserName: string;
}

export interface InvoiceTimesheet {
  timesheetId: string;
  contractorId: string;
  contractorName: string;
  weekStartDate: string;
  weekEndDate: string;
  totalHours: number;
  totalAmount: number;
  status: string;
}

export interface RecordInvoiceDto {
  amount: number; // required, min: 0.01
  paymentMethod: PaymentMethodEnum; // required
  referenceNumber?: string; // optional, maxLength: 100
  paymentDate?: string; // optional, ISO 8601 datetime
  notes?: string; // optional, maxLength: 500
}

export enum InvoiceStatus {
  DRAFT = 'Draft',
  SENT = 'Sent',
  VIEWED = 'Viewed',
  PARTIALLY_PAID = 'PartiallyPaid',
  PAID = 'Paid',
  OVERDUE = 'Overdue',
  CANCELLED = 'Cancelled',
  DISPUTED = 'Disputed'
}

export interface InvoicesSummary {
  periodStartDate: string;
  periodEndDate: string;
  payPeriodFormatted: string;
  totalInvoices: number;
  totalAmount: number;
  paidAmount: number;
  outstandingBalance: number;
  paidInvoicesCount: number;
  unpaidInvoicesCount: number;
  overdueInvoicesCount: number;
  overdueAmount: number;
  totalTimesheetsCount: number;
  statusBreakdown: StatusBreakdown[];
  invoices: FacilityInvoiceDetail[] | null;
  hoursScheduled: number;
  hoursWorked: number;
}
export interface StatusBreakdown {
  status: string;
  statusDisplay: string;
  count: number;
  totalAmount: number;
}
